/**
 * Utility functions for role and permission checks
 */
import { STORAGE_KEYS } from './constants';

/**
 * Check if a user is an admin
 * Admin access is granted if ANY of these is true:
 * - user.is_staff === true
 * - user.is_superuser === true
 * - user.role_name === 'admin'
 * 
 * @param {Object} user - User object
 * @returns {boolean} - True if user is admin
 */
export const isAdmin = (user) => {
  if (!user) return false;
  return (
    user.is_staff === true ||
    user.is_superuser === true ||
    user.role_name === 'admin'
  );
};

/**
 * Check if a user is an employee
 * @param {Object} user - User object
 * @returns {boolean} - True if user is employee
 */
export const isEmployee = (user) => {
  if (!user) return false;
  return user.role_name === 'employee';
};

/**
 * Check if a user is an employer
 * @param {Object} user - User object
 * @returns {boolean} - True if user is employer
 */
export const isEmployer = (user) => {
  if (!user) return false;
  return user.role_name === 'employer';
};

/**
 * Check if a user is verified by admin
 * @param {Object} user - User object
 * @returns {boolean} - True if user is verified
 */
export const isVerified = (user) => {
  if (!user) return false;
  return user.is_verified_by_admin === true;
};

const getCachedContractAcceptance = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEYS.CONTRACT_ACCEPTANCE);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    return null;
  }
};

const getCachedActiveContract = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEYS.ACTIVE_CONTRACT);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    return null;
  }
};

export const hasAcceptedContract = (user) => {
  if (!user) return false;

  const activeContract = getCachedActiveContract();
  if (activeContract?.missing === true) {
    return true;
  }
  if (!activeContract?.contract_key || !activeContract?.version) {
    return false;
  }

  const acceptedFromUser =
    user.contract_key === activeContract.contract_key &&
    user.contract_version === activeContract.version &&
    !!user.contract_accepted_at &&
    !!user.contract_signed_name;

  if (acceptedFromUser) {
    return true;
  }

  const cached = getCachedContractAcceptance();
  return (
    cached?.contractKey === activeContract.contract_key &&
    cached?.version === activeContract.version &&
    !!cached?.acceptedAt &&
    !!cached?.signedName
  );
};

/**
 * Get redirect path based on user role and verification status
 * @param {Object} user - User object
 * @returns {string} - Redirect path
 */
export const getRedirectPath = (user) => {
  if (!user) return '/login';
  
  // Admin always goes to admin panel
  if (isAdmin(user)) {
    return '/admin/dashboard';
  }
  
  // Employee or Employer
  if (isEmployee(user) || isEmployer(user)) {
    // If email verified but contract not accepted, go to contract gate
    if (user.is_email_verified === true && !hasAcceptedContract(user)) {
      return '/contract';
    }
    // If verified, go to dashboard (calendar view)
    if (isVerified(user)) {
      return '/dashboard';
    }
    // If not verified, go to pending verification
    return '/pending-verification';
  }
  
  // Default fallback
  return '/dashboard';
};


